import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { To, useNavigate } from 'react-router-dom'

import { Button, H4, Icon, Intent, Section } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import useAuth from '../../aws/useAuth'
import me from '../../domain/api/me'
import { GetCardResponseEntity } from '../../domain/entities/card'
import ApiError from '../common/apiError/ApiError'
import Empty from '../common/empty/Empty'
import Mount from '../common/mount/Mount'
import styles from './Cards.module.scss'
import CardsSkeleton from './CardsSkeleton'

export default (props: {
    redirectWhenNotAuthenticated: To,
    cardLink: (cardId: string) => To,
    profileLink: (profileId: string) => To,
}) => {
    const auth = useAuth()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const cardsResponse = me.cards.useFetchSWR()

    useEffect(() => {
        if (auth.isLoaded && !auth.isAuthenticated) navigate(props.redirectWhenNotAuthenticated)
    }, [auth, navigate, props])

    const linkIcon = useCallback((card: GetCardResponseEntity) => typeof card.profile_id === 'string'
        ? <Icon icon={IconNames.Link} intent={Intent.PRIMARY} onClick={() => navigate(props.profileLink(card.profile_id ?? ''))} />
        : <Icon icon={IconNames.Unlink} intent={Intent.NONE} />
        , [navigate, props])

    return <>
        <H4 className={styles.title}>{t('cards.title')}</H4>
        <div className={styles.verticalContainer}>
            <Mount response={cardsResponse}
                loading={() => <CardsSkeleton />}
                failure={error => <ApiError error={error} />}
                empty={() =>
                    <Empty
                        lottie="card"
                        title={t('cards.empty.label')}
                        description={t('cards.empty.description')}
                    />}
                success={cards => <>
                    {[...cards] // sort mutates the original array and can cause an error
                        .sort((a, b) => a.updated_at - b.updated_at)
                        .map((card, index) =>
                            <Section
                                key={`section-${index}`}
                                icon={linkIcon(card)}
                                title={card.profile_id ? t('cards.linkedItem.title') : t('cards.unlinkedItem.title')}
                                subtitle={t('cards.lastUpdated.label', {
                                    date: new Date(card.updated_at * 1_000),
                                })}
                                rightElement={
                                    <Button
                                        text={t('cards.detailButton.label')}
                                        icon={IconNames.DocumentOpen}
                                        onClick={() => navigate(props.cardLink(card.id))}
                                        intent={Intent.PRIMARY}
                                        minimal />}
                            />
                        )}
                </>}
            />
        </div>
    </>
}
